import { type ReactNode, useMemo } from "react";
import { SideMenuContext } from "@/app/layouts/SideMenuContext";
import { reduceSideMenuOpen } from "@/app/layouts/sideMenuState";
import {
  createMediator,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

interface SideMenuProviderProps {
  children: ReactNode;
}

export const SideMenuProvider: React.FunctionComponent<
  SideMenuProviderProps
> = ({ children }: SideMenuProviderProps) => {
  const mediator = useMemo(
    () =>
      createMediator(false, (state: boolean, event: TypedEvent) => {
        switch (event.type) {
          case "side-menu.open":
            return {
              status: "handled",
              state: reduceSideMenuOpen(state, "open"),
            };
          case "side-menu.close":
            return {
              status: "handled",
              state: reduceSideMenuOpen(state, "close"),
            };
          default:
            return { status: "unhandled" };
        }
      }),
    [],
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) =>
    mediator.dispatch(event).status === "handled" ? "handled" : "unhandled",
  );
  const isSideMenuOpen = useMediatorSnapshot(mediator);
  const controls = useMemo(
    () => ({
      openSideMenu: (): void => {
        node.dispatch({ type: "side-menu.open" });
      },
      closeSideMenu: (): void => {
        node.dispatch({ type: "side-menu.close" });
      },
    }),
    [node],
  );
  const value = useMemo(
    () => ({ isSideMenuOpen, ...controls }),
    [controls, isSideMenuOpen],
  );

  return (
    <ComponentScope node={node}>
      <SideMenuContext.Provider value={value}>
        {children}
      </SideMenuContext.Provider>
    </ComponentScope>
  );
};
